import { useState, useEffect } from 'react';
import { api } from '../../api/client';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Loading from '../../components/common/Loading';

interface IncentiveRates {
  staffRate: number;
  pmRate: number;
  promoterRate: number;
}

export default function IncentiveSettings() {
  const [rates, setRates] = useState<IncentiveRates>({
    staffRate: 0.04,
    pmRate: 0.01,
    promoterRate: 0.02,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const data = await api.get<IncentiveRates>('/api/settings/incentive-rates');
      setRates(data);
    } catch (error) {
      console.error('Failed to fetch incentive rates:', error);
      setMessage({ type: 'error', text: '設定の読み込みに失敗しました' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof IncentiveRates, value: string) => {
    const numValue = parseFloat(value) / 100; // パーセンテージから小数に変換
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 1) {
      setRates(prev => ({ ...prev, [field]: numValue }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setMessage(null);
    
    try {
      await api.put('/api/settings/incentive_rates', {
        value: rates,
        description: '工事登録時のデフォルトインセンティブ率',
      });
      setMessage({ type: 'success', text: 'インセンティブ率を更新しました' });
    } catch (error) {
      console.error('Failed to save incentive rates:', error);
      setMessage({ type: 'error', text: '保存に失敗しました' });
    } finally {
      setSaving(false);
    }
  };

  const formatPercent = (value: number): string => {
    return (value * 100).toFixed(2);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loading size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">インセンティブ設定</h1>
      </div>

      <Card>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
              ℹ️ インセンティブ計算について
            </h3>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              工事登録時、紹介手数料に各インセンティブ率を掛けてインセンティブ額が自動計算されます。<br />
              設定変更は新規工事のみに適用され、既存の工事には影響しません。
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white">担当者</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">工事の担当者</p>
                </div>
              </div>
              <div className="relative">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formatPercent(rates.staffRate)}
                  onChange={(e) => handleChange('staffRate', e.target.value)}
                  className="pr-8"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
              </div>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                例: 紹介手数料 ¥100,000 → インセンティブ ¥{Math.round(100000 * rates.staffRate).toLocaleString()}
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white">PM</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">プロジェクトマネージャー</p>
                </div>
              </div>
              <div className="relative">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formatPercent(rates.pmRate)}
                  onChange={(e) => handleChange('pmRate', e.target.value)}
                  className="pr-8"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
              </div>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                例: 紹介手数料 ¥100,000 → インセンティブ ¥{Math.round(100000 * rates.pmRate).toLocaleString()}
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white">プロモーター</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">営業担当者</p>
                </div>
              </div>
              <div className="relative">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formatPercent(rates.promoterRate)}
                  onChange={(e) => handleChange('promoterRate', e.target.value)}
                  className="pr-8"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
              </div>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                例: 紹介手数料 ¥100,000 → インセンティブ ¥{Math.round(100000 * rates.promoterRate).toLocaleString()}
              </p>
            </div>
          </div>

          {/* 合計率表示 */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">合計インセンティブ率</span>
              <span className="text-lg font-bold text-gray-900 dark:text-white">
                {formatPercent(rates.staffRate + rates.pmRate + rates.promoterRate)}%
              </span>
            </div>
            <div className="mt-2 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div className="h-full flex">
                <div 
                  className="bg-green-500"
                  style={{ width: `${rates.staffRate * 100 / (rates.staffRate + rates.pmRate + rates.promoterRate || 1) * 100}%` }}
                />
                <div 
                  className="bg-purple-500"
                  style={{ width: `${rates.pmRate * 100 / (rates.staffRate + rates.pmRate + rates.promoterRate || 1) * 100}%` }}
                />
                <div 
                  className="bg-orange-500"
                  style={{ width: `${rates.promoterRate * 100 / (rates.staffRate + rates.pmRate + rates.promoterRate || 1) * 100}%` }}
                />
              </div>
            </div>
            <div className="mt-2 flex gap-4 text-xs text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 bg-green-500 rounded-full" />
                担当者
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 bg-purple-500 rounded-full" />
                PM
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 bg-orange-500 rounded-full" />
                プロモーター
              </span>
            </div>
          </div>

          {message && (
            <div className={`p-4 rounded-lg ${
              message.type === 'success' 
                ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-800'
                : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800'
            }`}>
              {message.text}
            </div>
          )}

          <div className="flex justify-end">
            <Button type="submit" disabled={saving}>
              {saving ? '保存中...' : '設定を保存'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
